// Device detection utilities

/**
 * Check if the current device is mobile
 * @returns {boolean} true if mobile device
 */
export const isMobileDevice = () => {
  // Check user agent for mobile indicators
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;
  
  // Mobile user agent patterns
  const mobilePatterns = [
    /Android/i,
    /webOS/i,
    /iPhone/i,
    /iPad/i,
    /iPod/i,
    /BlackBerry/i,
    /Windows Phone/i,
    /Mobile/i,
    /Tablet/i
  ];
  
  // Check if any mobile pattern matches
  const isMobileUA = mobilePatterns.some(pattern => pattern.test(userAgent));
  
  // Check screen size (mobile-like dimensions)
  const isMobileScreen = window.innerWidth <= 768 || window.innerHeight <= 768;
  
  // Check for touch support
  const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  
  // Combine checks - device is mobile if it matches UA patterns OR has mobile screen + touch
  return isMobileUA || (isMobileScreen && isTouchDevice);
};

/**
 * Check if the current device is tablet
 * @returns {boolean} true if tablet device
 */
export const isTabletDevice = () => {
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;
  
  // Tablet-specific patterns
  const tabletPatterns = [
    /iPad/i,
    /Android(?!.*Mobile)/i, // Android tablets don't have "Mobile" in UA
    /Tablet/i
  ];
  
  const isTabletUA = tabletPatterns.some(pattern => pattern.test(userAgent));
  
  // Check for tablet-like screen dimensions
  const isTabletScreen = (
    (window.innerWidth >= 768 && window.innerWidth <= 1024) ||
    (window.innerHeight >= 768 && window.innerHeight <= 1024)
  ) && 'ontouchstart' in window;
  
  return isTabletUA || isTabletScreen;
};

/**
 * Check if the current device is desktop
 * @returns {boolean} true if desktop device
 */
export const isDesktopDevice = () => {
  return !isMobileDevice() && !isTabletDevice();
};

/**
 * Get device type as string
 * @returns {'mobile' | 'tablet' | 'desktop'} device type
 */
export const getDeviceType = () => {
  if (isMobileDevice()) return 'mobile';
  if (isTabletDevice()) return 'tablet';
  return 'desktop';
};

/**
 * Check if device should use fullscreen mode
 * For mobile devices, fullscreen provides better UX
 * @returns {boolean} true if device should use fullscreen
 */
export const shouldUseFullscreen = () => {
  return isMobileDevice();
};

/**
 * Get optimal viewport settings for current device
 * @returns {object} viewport configuration
 */
export const getViewportConfig = () => {
  const deviceType = getDeviceType();
  
  return {
    deviceType,
    shouldExpand: shouldUseFullscreen(),
    preferFullscreen: deviceType === 'mobile',
    allowExpansion: deviceType !== 'desktop'
  };
}; 